import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

import { 
  Zap, 
  Droplets, 
  Building, 
  Factory, 
  Train, 
  Radio, 
  Mountain, 
  Cpu, 
  ArrowRight,
  Globe,
  Users,
  Award,
  TrendingUp,
  CheckCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../utils/cn';

const Industries = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  // Image slider component
  const ImageSlider = ({ images, autoPlay = true, interval = 3000 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      if (!autoPlay || images.length <= 1) return;

      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, interval);

      return () => clearInterval(timer);
    }, [autoPlay, interval, images.length]);

    const goToPrevious = () => {
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    };

    const goToNext = () => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    };

    if (!images || images.length === 0) return null;

    return (
      <div className="relative w-full h-48 overflow-hidden rounded-t-lg group">
        <div 
          className="flex transition-transform duration-500 ease-in-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <div key={index} className="w-full h-full flex-shrink-0 relative">
              <img
                src={image}
                alt={`Industry image ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-global-dark-blue/20" />
            </div>
          ))}
        </div>
        
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-global-dark-blue rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-global-dark-blue rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-white shadow-lg' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const industries = [
    {
      icon: Zap,
      title: 'Oil & Gas',
      description: 'Comprehensive solutions for upstream, midstream, and downstream operations',
      projects: '150+',
      solutions: ['Hazardous Area Equipment', 'Process Control Systems', 'Safety Solutions', 'Communication Networks'],
      gradient: 'from-global-green to-global-dark-blue',
      images: [
        '/divisions/Oil and Gas/elevated-night-view-of-an-oil-refinery-complex-2024-07-29-20-20-48-utc.JPG',
        '/divisions/Oil and Gas/oil-drilling-rig-construction-site-2023-11-27-05-36-31-utc.jpg',
        '/divisions/Oil and Gas/oil-refinery-2023-11-27-05-36-05-utc.jpg',
        '/divisions/Oil and Gas/landscape-with-nature-ecology-and-industry-2023-11-27-05-28-20-utc.jpg'
      ]
    },
    {
      icon: Radio,
      title: 'Telecommunication',
      description: 'Cutting-edge telecom infrastructure and communication solutions',
      projects: '120+',
      solutions: ['Network Infrastructure', 'Wireless Solutions', 'Fiber Optics', 'Data Centers'],
      gradient: 'from-global-light-blue to-global-dark-blue',

      images: [
        '/divisions/Telecommunications/communications-tower-2024-10-22-17-32-42-utc.jpg',
        '/divisions/Telecommunications/data-center-with-multiple-rows-of-fully-operationa-2023-11-27-05-16-14-utc.jpg',
        '/divisions/Telecommunications/fiber-optics-2024-10-21-03-46-49-utc.jpg',
        '/divisions/Telecommunications/telecommunication-equipment-for-5g-radio-network-2024-12-03-04-12-51-utc.jpg'
      ]
    },
    {
      icon: Zap,
      title: 'Power',
      description: 'Advanced power generation and distribution solutions',
      projects: '80+',
      solutions: ['Grid Management', 'Renewable Integration', 'Power Quality', 'Smart Metering'],
      gradient: 'from-global-green to-global-light-blue',

      images: [
        '/divisions/Power - Solar/solar-panel-on-blue-sky-background-2024-10-20-08-49-03-utc.jpg',
        '/divisions/Power - Solar/solar-energy-2023-11-27-05-15-11-utc.jpg',
        '/divisions/Power - Solar/solar-panels-on-a-farm-2023-11-27-05-21-16-utc.jpg'
      ]
    },
    {
      icon: Droplets,
      title: 'Water',
      description: 'Advanced systems for water treatment, distribution, and management',
      projects: '80+',
      solutions: ['SCADA Systems', 'Flow Monitoring', 'Quality Control', 'Remote Monitoring'],
      gradient: 'from-global-light-blue to-global-green',

      images: [
        '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg',
        '/divisions/Commercial - Electrical/multizone-air-conditioning-and-ventilation-system-2023-11-27-05-35-39-utc.jpg',
        '/divisions/Commercial - Electrical/pile-of-pipes-and-parts-for-duct-systems-industri-2023-11-27-04-52-03-utc.jpg'
      ]
    },
    {
      icon: Factory,
      title: 'Petrochemicals',
      description: 'Specialized solutions for chemical processing and manufacturing',
      projects: '90+',
      solutions: ['Process Automation', 'Safety Systems', 'Environmental Monitoring', 'Quality Assurance'],
      gradient: 'from-global-green to-global-light-blue',

      images: [
        '/divisions/Hazardous/chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-18-08-utc.jpg',
        '/divisions/Hazardous/industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg',
        '/divisions/Hazardous/industrial-factory-2023-11-27-05-17-09-utc.jpg',
        '/divisions/Hazardous/detail-of-chemical-plant-in-twilight-2023-11-27-05-32-28-utc.jpg'
      ]
    },
    {
      icon: Mountain,
      title: 'Mining',
      description: 'Robust solutions for harsh mining environments and operations',
      projects: '60+',
      solutions: ['Underground Communication', 'Safety Monitoring', 'Equipment Control', 'Environmental Systems'],
      gradient: 'from-global-green to-global-light-blue',

      images: [
        '/divisions/General/industrial-stone-mining-truck-in-the-middle-of-h-2023-11-27-04-58-18-utc.jpg',
        '/divisions/Hazardous/industrial-factory-2023-11-27-05-17-09-utc.jpg'
      ]
    },
    {
      icon: Building,
      title: 'Infrastructure',
      description: 'Smart infrastructure solutions for modern cities and developments',
      projects: '200+',
      solutions: ['Smart Buildings', 'Traffic Management', 'Public Safety', 'Energy Management'],
      gradient: 'from-global-dark-blue to-global-light-blue',

      images: [
        '/divisions/General/city-overpass-at-night-2023-11-27-05-10-18-utc.jpg',
        '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg',
        '/divisions/General/metro-railway-train-in-dubai-city-in-uae-2023-11-27-05-18-15-utc.jpg'
      ]
    },
    {
      icon: Train,
      title: 'Railway',
      description: 'Advanced railway communication and signaling systems',
      projects: '45+',
      solutions: ['Signaling Systems', 'Communication Networks', 'Passenger Information', 'Safety Systems'],
      gradient: 'from-global-dark-blue to-global-green',

      images: [
        '/divisions/General/metro-railway-train-in-dubai-city-in-uae-2023-11-27-05-18-15-utc.jpg',
        '/divisions/Commercial - Electrical/dubai-metro-2024-12-05-07-58-55-utc.jpg'
      ]
    },
    {
      icon: Cpu,
      title: 'Utilities',
      description: 'Comprehensive utility management and infrastructure solutions',
      projects: '70+',
      solutions: ['Smart Grid Systems', 'Utility Monitoring', 'Energy Management', 'Infrastructure Control'],
      gradient: 'from-global-dark-blue to-global-light-blue',

      images: [
        '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg',
        '/divisions/Commercial - Electrical/multizone-air-conditioning-and-ventilation-system-2023-11-27-05-35-39-utc.jpg',
        '/divisions/Commercial - Electrical/pile-of-pipes-and-parts-for-duct-systems-industri-2023-11-27-04-52-03-utc.jpg'
      ]
    }
  ];

  const stats = [
    {
      icon: Globe,
      number: '500+',
      label: 'Projects Completed',
      description: 'Successfully delivered across multiple industries'
    },
    {
      icon: Users,
      number: '200+',
      label: 'Satisfied Clients',
      description: 'Long-term partnerships built on trust and excellence'
    },
    {
      icon: Award,
      number: '25+',
      label: 'Years Experience',
      description: 'Decades of expertise in technology solutions'
    },
    {
      icon: TrendingUp,
      number: '99%',
      label: 'Success Rate',
      description: 'Proven track record of project success'
    }
  ];

  const clientLogos = [
    { name: 'ADNOC', logo: '/Our client logos/1. ADNOC.png' },
    { name: 'ADWEA', logo: '/Our client logos/2.ADWEA.png' },
    { name: 'ADDC', logo: '/Our client logos/3. ADDC.png' },
    { name: 'Abu Dhabi Airports', logo: '/Our client logos/4. abu-dhabi-airports.png' },
    { name: 'Borouge', logo: '/Our client logos/4a BOROUGE LOGO.png' },
    { name: 'DEWA', logo: '/Our client logos/5. DEWA-logo.jpeg' },
    { name: 'SEWA', logo: '/Our client logos/6. sewa_logo.png' },
    { name: 'CPECC', logo: '/Our client logos/7. CPECC.jpeg' },
    { name: 'ENEC', logo: '/Our client logos/8. ENEC logo.jpg' },
    { name: 'Dragon Oil', logo: '/Our client logos/9. dragon oil.png' },
    { name: 'Saipem', logo: '/Our client logos/10. Saipem_logo.png' },
    { name: 'Alderley', logo: '/Our client logos/11. Alderley.jpg' },
    { name: 'Petrofac', logo: '/Our client logos/12. Petrofac.png' },
    { name: 'Robtstone', logo: '/Our client logos/13. robtstone.jpeg' },
    { name: 'AlASAB', logo: '/Our client logos/14. AlASAB.jpeg' },
    { name: 'Saudi Aramco', logo: '/Our client logos/15. Saudi Aramco.jpeg' },
    { name: 'SATORP', logo: '/Our client logos/16. SATORP.png' },
    { name: 'SABIC', logo: '/Our client logos/17. SABIC.png' },
    { name: 'Sadara', logo: '/Our client logos/18. Sadara.jpeg' },
    { name: 'SAPCO', logo: '/Our client logos/19. SAPCO.jpeg' },
    { name: 'KJO', logo: '/Our client logos/20. KJO logo.jpeg' },
    { name: 'MEW Kuwait', logo: '/Our client logos/21. MEW kuwait.jpeg' },
    { name: 'MAADEN', logo: '/Our client logos/22. MAADEN.png' },
    { name: 'PETRONAS', logo: '/Our client logos/23. PETRONAS.png' },
    { name: 'KBR', logo: '/Our client logos/24. KBR.png' },
    { name: 'ABB', logo: '/Our client logos/25. ABB.png' },
    { name: 'PETRONASH', logo: '/Our client logos/PETRONASH.png' },
    { name: 'SWCC', logo: '/Our client logos/SWCC.jpg' }
  ];

  const capabilities = [
    {
      title: 'Industry Expertise',
      description: 'Deep understanding of industry-specific requirements and challenges',
      features: ['Regulatory Compliance', 'Best Practices', 'Industry Standards', 'Risk Management']
    },
    {
      title: 'Technical Excellence',
      description: 'Advanced technical capabilities and cutting-edge solutions',
      features: ['Latest Technologies', 'Custom Solutions', 'Integration Expertise', 'Quality Assurance']
    },
    {
      title: 'Project Management',
      description: 'Proven project management methodologies and delivery excellence',
      features: ['On-time Delivery', 'Budget Management', 'Risk Mitigation', 'Quality Control']
    },
    {
      title: 'Support Services',
      description: 'Comprehensive support and maintenance services',
      features: ['24/7 Support', 'Preventive Maintenance', 'Training Programs', 'Technical Documentation']
    }
  ];

  return (
    <div className="min-h-screen pt-10">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden text-white h-[80vh] flex items-center">
        {/* Enhanced Background with Blackish Gradient */}
        <div className="absolute inset-0 bg-global-dark-blue"></div>
        
        {/* Geometric Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 20px,
                rgba(255,255,255,0.3) 20px,
                rgba(255,255,255,0.3) 22px,
                transparent 22px,
                transparent 40px,
                rgba(255,255,255,0.2) 40px,
                rgba(255,255,255,0.2) 42px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 30px,
                rgba(255,255,255,0.2) 30px,
                rgba(255,255,255,0.2) 32px,
                transparent 32px,
                transparent 60px,
                rgba(255,255,255,0.1) 60px,
                rgba(255,255,255,0.1) 62px
              )
            `,
            backgroundSize: '80px 80px, 100px 100px'
          }}
        ></div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 4) * 20}%`,
                animation: `float ${4 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`
              }}
            ></div>
          ))}
        </div>
        
        <div className="container-max relative z-8">
          <motion.div
            className="text-center max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            
            {/* Enhanced Lottie Animation Container */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <motion.h1
                className="relative flex justify-center items-center text-4xl md:text-5xl lg:text-6xl font-bold mb-6 pt-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                {/* Glow Effect Behind Animation */}
                <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl scale-75"></div>
                Industries & Clients 
              </motion.h1>
            </motion.div>
            
            <motion.p 
              className="text-xl md:text-2xl lg:text-3xl mb-10 leading-relaxed text-white/90 max-w-4xl mx-auto font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              Delivering specialized technology solutions across diverse industries with proven expertise and innovation.
            </motion.p>

            {/* Call-to-Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/contact">
                  <div className="px-6 py-4 bg-white text-global-dark-blue font-semibold rounded-lg hover:bg-global-light-blue transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                    Discuss Your Project
                  </div>
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/services">
                  <div className="px-8 py-4 border-2 border-white/60 text-white hover:bg-white/10 hover:border-white font-semibold rounded-lg transition-all duration-300">
                    Our Capabilities
                  </div>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Delivering specialized technology solutions across diverse industries with proven expertise and innovation.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                >
                  <Card className="h-full hover:scale-105 transition-transform duration-300 group relative overflow-hidden bg-white shadow-lg ">
                    {/* Image Slider */}
                    <ImageSlider images={industry.images} />
                    
                    
                    <CardHeader className="relative z-10 pt-6">
                      <div className={`w-16 h-16 bg-global-green rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-lg text-charcoal">{industry.title}</CardTitle>
                        <span className="text-sm font-semibold text-global-green bg-global-green/10 border border-global-green/20 px-3 py-1 rounded-full">
                          {industry.projects}
                        </span>
                      </div>
                      <CardDescription className="text-slate">{industry.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="relative z-10 pb-6">
                      <div className="space-y-2 mb-4">
                        {industry.solutions.map((solution, solutionIndex) => (
                          <div key={solutionIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-3 h-3 text-global-green mr-2 flex-shrink-0" />
                            {solution}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Capabilities
            </h2>
            <p className="text-xl text-global-green max-w-3xl mx-auto">
              Comprehensive capabilities that enable us to deliver exceptional results across all industries.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className="h-full relative overflow-hidden bg-global-green text-white border-0 shadow-2xl min-h-[320px]">
                  {/* Geometric Pattern Background */}
                  <div 
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: `
                        repeating-linear-gradient(
                          45deg,
                          transparent,
                          transparent 20px,
                          rgba(232, 232, 232, 0.99) 22px,
                          transparent 22px,
                          transparent 40px,
                          rgb(187, 202, 166) 40px,
                          rgba(233, 240, 223, 0.64) 42px
                        ),
                        repeating-linear-gradient(
                          -45deg,
                          transparent,
                          transparent 30px,
                          rgb(155, 197, 99) 30px,
                          rgba(233, 240, 223, 0.64) 32px,
                          transparent 32px,
                          transparent 60px,
                          rgb(187, 202, 166) 60px,
                          rgba(233, 240, 223, 0.64) 62px
                        )
                      `,
                      backgroundSize: '80px 80px, 100px 100px'
                    }}
                  />
                  
                  {/* Additional Grid Lines */}
                  <div 
                    className="absolute inset-0 opacity-15"
                    style={{
                      backgroundImage: `
                        repeating-linear-gradient(
                          0deg,
                          transparent,
                          transparent 50px,
                          rgba(255,255,255,0.15) 50px,
                          rgba(255, 255, 255, 0.94) 51px
                        ),
                        repeating-linear-gradient(
                          90deg,
                          transparent,
                          transparent 50px,
                          rgba(255, 255, 255, 0.94) 50px,
                          rgba(255,255,255,0.15) 51px
                        )
                      `
                    }}
                  />

                  <CardHeader className="relative z-10 pb-4">
                    <CardTitle className="text-2xl text-white font-bold">{capability.title}</CardTitle>
                    <CardDescription className="text-white text-base leading-relaxed">{capability.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <div className="grid grid-cols-1 gap-4">
                      {capability.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-white">
                          <CheckCircle className="w-5 h-5 text-white mr-3 flex-shrink-0" />
                          <span className="text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Our Trusted Clients
            </h2>
            <p className="text-xl max-w-3xl mx-auto text-slate">
              We are proud to work with leading companies across various industries worldwide.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {clientLogos.map((client, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:border-global-green hover:shadow-lg hover:scale-105 transition-all duration-300 group"
                variants={fadeInUp}
              >
                <div className="aspect-square flex items-center justify-center">
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="max-w-full max-h-full object-contain transition-all duration-300"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-global-green/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-global-green z-5 w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 20px,
                        rgba(255,255,255,0.3) 20px,
                        rgba(255,255,255,0.3) 22px,
                        transparent 22px,
                        transparent 40px,
                        rgba(255,255,255,0.2) 40px,
                        rgba(255,255,255,0.2) 42px
                      ),
                      repeating-linear-gradient(
                        -45deg,
                        transparent,
                        transparent 30px,
                        rgba(255,255,255,0.2) 30px,
                        rgba(255,255,255,0.2) 32px,
                        transparent 32px,
                        transparent 60px,
                        rgba(255,255,255,0.1) 60px,
                        rgba(255,255,255,0.1) 62px
                      )
                    `,
                    backgroundSize: '80px 80px, 100px 100px'
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      ),
                      repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      )
                    `
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="inline-block mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30">
                    Ready to Get Started?
                  </span>
                </motion.div>
                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Ready to Start Your Project?
                </motion.h2>
                
                <motion.p 
                  className="text-base lg:text-lg mb-6 text-white leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Get in touch with our experts to discuss your engineering needs
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                        {t('common.contactUs')}
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/services">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm transition-all duration-300">
                        {t('common.getQuote')}
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;